/** Re-authentication dialog for dangerous actions (D1-T10).
 *
 * Before critical actions (emergency stop, trading mode switch, config changes),
 * checks if TOTP was verified recently. If >1 hour, shows a TOTP modal.
 * On success, proceeds with the original action. On cancel, aborts.
 */

import { useState, useCallback, type FormEvent } from 'react'
import * as api from '../../api/client'

const REAUTH_WINDOW_MS = 60 * 60 * 1000
type ReauthAction = () => void | Promise<void>

export interface ReauthDialogProps {
  isOpen: boolean
  onVerified: () => void | Promise<void>
  onCancel: () => void
  /** Action description shown in the dialog, e.g. "activate emergency stop" */
  actionDescription?: string
}

export function ReauthDialog({
  isOpen,
  onVerified,
  onCancel,
  actionDescription = 'perform this action',
}: ReauthDialogProps) {
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (code.length !== 6) return

    setIsVerifying(true)
    setError(null)

    try {
      await api.reauthTotp(code)
      setCode('')
      await onVerified()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid TOTP code. Please try again.')
    } finally {
      setIsVerifying(false)
    }
  }

  const handleCancel = () => {
    setCode('')
    setError(null)
    onCancel()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-label="Re-authentication required"
    >
      <div className="w-full max-w-sm rounded-lg bg-gray-800 p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-semibold text-gray-100">Re-authentication Required</h2>
        <p className="mb-4 text-sm text-gray-400">Enter your TOTP code to {actionDescription}.</p>

        {error && (
          <div className="mb-3 rounded bg-red-900/50 px-3 py-2 text-sm text-red-300">{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="mb-4 w-full rounded bg-gray-700 px-3 py-2 text-center text-lg tracking-widest text-gray-100 outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="000000"
            autoFocus
            disabled={isVerifying}
            aria-label="TOTP code"
          />

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isVerifying}
              className="flex-1 rounded bg-gray-600 px-4 py-2 text-sm font-medium text-gray-200 transition-colors hover:bg-gray-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isVerifying || code.length !== 6}
              className="flex-1 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {isVerifying ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── useReauth Hook ─────────────────────────────────────────────────────────

/** Tracks when TOTP was last verified, and shows re-auth dialog when needed. */
// eslint-disable-next-line react-refresh/only-export-components
export function useReauth() {
  const [lastVerified, setLastVerified] = useState<number>(0)
  const [pendingAction, setPendingAction] = useState<ReauthAction | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [actionDesc, setActionDesc] = useState<string | undefined>()

  /** Check if re-auth is needed, then execute action. */
  const requireReauth = useCallback(
    (action: ReauthAction, description?: string) => {
      const now = Date.now()
      if (now - lastVerified < REAUTH_WINDOW_MS) {
        // Recently verified — proceed immediately
        void Promise.resolve(action()).catch((err) => {
          console.error('Reauth action failed', err)
        })
      } else {
        // Need re-auth
        setPendingAction(() => action)
        setActionDesc(description)
        setIsDialogOpen(true)
      }
    },
    [lastVerified],
  )

  const handleVerified = useCallback(async () => {
    const action = pendingAction
    setLastVerified(Date.now())
    setIsDialogOpen(false)
    setPendingAction(null)
    setActionDesc(undefined)
    if (action) {
      await action()
    }
  }, [pendingAction])

  const handleCancel = useCallback(() => {
    setIsDialogOpen(false)
    setPendingAction(null)
    setActionDesc(undefined)
  }, [])

  return {
    requireReauth,
    reauthDialog: {
      isOpen: isDialogOpen,
      onVerified: handleVerified,
      onCancel: handleCancel,
      actionDescription: actionDesc,
    },
  }
}
